// `SentryExecutionTable` is a table showing a list of sentry executions for an entity/policy pair.

import React from "react";
import { Query } from "react-apollo";
import { Link } from "react-router-dom";
import { withApolloState } from "@arachnys/flow-app/src/util/apollo";
import Table from "@arachnys/styleguide/Table";
import gql from "graphql-tag";
import { formatDatetimeString } from "../../formatters/formatDateTimeString";
import { ENTITY_PROCESS_FRAGMENT } from "../../page/EntityDetail/queries";

export const GET_SENTRY_EXECUTIONS_QUERY = gql`
    query SentryExecutions($entityUlid: String!, $policyUlid: String!) {
        sentryEntityPolicyExecutions(
            entityUlid: $entityUlid
            policyUlid: $policyUlid
        ) {
            pageInfo {
                totalCount
                endCursor
                hasNextPage
            }
            edges {
                node {
                    __typename
                    ulid
                    status
                    result {
                        resultType
                        description
                        resultsCount
                    }
                    createdDate
                    process {
                        ...EntityProcess
                    }
                }
            }
        }
    }
    ${ENTITY_PROCESS_FRAGMENT}
`;

const ProcessLink = ({ entity, process }) => {
    if (!process) return null;
    const hasProcessHistory = process.hasProcessHistory && process.finished;
    const activeTask = process.activeTask;
    const isAssignable = activeTask && activeTask.assignable;

    if (isAssignable) {
        return (
            <Link to={`/tasks/${activeTask.uid}/claim`}>
                {activeTask.title}
            </Link>
        );
    } else if (activeTask) {
        return activeTask.title;
    } else if (hasProcessHistory) {
        return (
            <Link
                to={`/entities/${entity.key.ulid}/process-history/${process.uid}`}
            >
                {`Process History`}
            </Link>
        );
    } else if (process.finished) {
        return "Finished";
    }
    return "";
};

const renderSentryResultCell = ({ result: { resultType, resultsCount } }) => {
    switch (resultType) {
        case "ERROR":
            return "There has been an error";
        case "ALERTS_FOUND":
            return `${resultsCount} alerts found`;
        case "NO_ALERTS_FOUND":
            return "No alerts were found";
        case "MATERIAL_CHANGES_FOUND":
            return `Material changes found`;
        case "NO_MATERIAL_CHANGES_FOUND":
            return `No material changes found`;
        default:
            return null;
    }
};

export const SentryExecutionsTable = ({ entity, policy }) => {
    const executionColumns = [
        {
            id: "executiondate",
            Header: "Check date",
            accessor: "node.createdDate",
            Cell: row =>
                formatDatetimeString({ str: row.value, precision: "MINUTE" }),
        },
        {
            id: "results",
            Header: "Results",
            accessor: "node.result.resultType",
            Cell: row => renderSentryResultCell(row.original.node),
        },
        {
            id: "process",
            Header: "Process",
            accessor: "node.result",
            Cell: row =>
                ProcessLink({ entity, process: row.original.node.process }),
        },
    ];

    return (
        <Query
            variables={{ entityUlid: entity.key.ulid, policyUlid: policy.ulid }}
            query={GET_SENTRY_EXECUTIONS_QUERY}
            fetchPolicy="cache-and-network"
        >
            {withApolloState(apolloProps => {
                return (
                    <Table
                        data={
                            apolloProps.data.sentryEntityPolicyExecutions
                                .edges || []
                        }
                        columns={executionColumns}
                        minRows={3}
                        noDataText="No policy history found"
                    />
                );
            })}
        </Query>
    );
};
